<template>
  <div class="payment-rules-form">
    <!-- Action buttons for existing rule -->
    <div v-if="isEditMode && props.rule?.id" class="action-buttons">
      <Button
        data-testid="payroll-payment-rules-form-deactivate-button"
        @click="handleDeactivate"
        severity="warning"
        icon="pi pi-ban"
        :label="t('Desativar regra')"
        :disabled="!props.rule?.active"
      />
      <Button
        data-testid="payroll-payment-rules-form-delete-button"
        @click="handleDelete"
        severity="danger"
        icon="pi pi-trash"
        :label="t('Excluir regra')"
      />
    </div>

    <form @submit.prevent="handleSubmit">
      <div class="p-fluid">
        <!-- User Selection -->
        <div class="p-field">
          <label for="user_id">{{ t('Colaborador') }} *</label>
          <select-list
            elem-id="payroll_form_user_id"
            data-testid="payroll-payment-rules-form-user-select"
            id="user_id"
            v-model="formData.user_id"
            :itens-select="employees"
            :placeholder="t('Selecione o Colaborador')"
            :class="{ 'p-invalid': !isUserIdValid }"
            :disabled="isEditMode"
          />
          <small v-if="!isUserIdValid" class="p-error">
            {{ t('Colaborador é obrigatório') }}
          </small>
        </div>

        <!-- Franchise Selection -->
        <div class="p-field">
          <label for="franchise_id">{{ t('Franquia') }} *</label>
          <franchise-select
            data-testid="payroll-payment-rules-form-franchise-select"
            id="franchise_id"
            v-model="formData.franchise_id"
            :placeholder="t('Selecione a franquia')"
            :class="{ 'p-invalid': !isFranchiseIdValid }"
            :disabled="isEditMode"
          />
          <small v-if="!isFranchiseIdValid" class="p-error">
            {{ t('Franquia é obrigatória') }}
          </small>
        </div>

        <!-- Rule Type -->
        <div class="p-field">
          <label for="rule_type">{{ t('Tipo de regra') }} *</label>
          <select-list
            elem-id="payroll_rule_type"
            data-testid="payroll-payment-rules-form-rule-type-select"
            id="rule_type"
            v-model="formData.rule_type"
            :itens-select="ruleTypes"
            :placeholder="t('Selecione o tipo de regra')"
            :class="{ 'p-invalid': !isRuleTypeValid }"
            :disabled="isEditMode"
          />
          <small v-if="!isRuleTypeValid" class="p-error">
            {{ t('Tipo de regra é obrigatório') }}
          </small>
        </div>

        <!-- Commission Structure Type (only for commission rules) -->
        <div v-if="isCommissionRule" class="p-field">
          <label for="commission_structure_type">{{ t('Estrutura de Comissão') }} *</label>
          <select-list
            elem-id="payroll_commission_structure_type"
            data-testid="payroll-payment-rules-form-structure-select"
            id="commission_structure_type"
            v-model="commissionStructureType"
            :itens-select="commissionStructureTypes"
            :placeholder="t('Selecione a estrutura')"
            :disabled="isEditMode"
          />
          <small class="p-help">
            {{ getCommissionStructureHelp() }}
          </small>
        </div>

        <!-- Period Type (for FIXED rules only) -->
        <div v-if="formData.rule_type === 'fixed'" class="p-field">
          <label for="period_type">{{ t('Tipo de período') }} *</label>
          <select-list
            elem-id="payroll_form_period_type"
            data-testid="payroll-payment-rules-form-period-select"
            id="period_type"
            v-model="period_type"
            :itens-select="periodTypes"
            :placeholder="t('Selecione o tipo de período')"
            :class="{ 'p-invalid': !isPeriodTypeValid }"
            :disabled="isEditMode"
          />
          <small class="p-help">
            {{ t('Usado para cálculo proporcional do salário fixo') }}
          </small>
          <small v-if="!isPeriodTypeValid" class="p-error">
            {{ t('Tipo de período é obrigatório') }}
          </small>
        </div>

        <!-- Amount (Simple Commission or Fixed Salary) -->
        <div v-if="!isCommissionRule || commissionStructureType === 'simple'" class="p-field">
          <label for="amount">
            {{ isCommissionRule ? t('Percentual de comissão') : t('Valor fixo') }} *
          </label>
          <input-currency
            v-if="!isCommissionRule"
            elem-id="payroll_form_amount"
            data-testid="payroll-payment-rules-form-amount-currency"
            id="amount"
            v-model="formData.amount"
            :placeholder="'R$ 0,00'"
            :class="{ 'p-invalid': !isAmountValid }"
          />
          <div v-else class="commission-slider-container">
            <div class="commission-value-display">
              {{ commissionPercentage }}%
            </div>
            <input-slider
              data-testid="payroll-payment-rules-form-commission-slider"
              elem-id="amount"
              v-model="commissionPercentage"
              :min="0"
              :max="100"
              :step="0.25"
              :class="{ 'p-invalid': !isAmountValid }"
            />
            <div class="slider-labels">
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
          </div>
          <small v-if="!isAmountValid" class="p-error">
            {{ isCommissionRule ? t('Comissão não pode exceder 100%') : t('Valor deve ser maior que 0') }}
          </small>
        </div>

        <!-- Bracket Editor (Tiered/Progressive Commission) -->
        <div v-if="isCommissionRule && commissionStructureType !== 'simple'" class="p-field">
          <label>{{ t('Faixas de Comissão') }} *</label>
          <div class="bracket-editor">
            <div v-for="(bracket, index) in brackets" :key="index" class="bracket-row">
              <div class="bracket-fields">
                <div class="field-group">
                  <label>{{ t('De') }}</label>
                  <input-currency
                    :elem-id="`payroll_bracket_min_${index}`"
                    :data-testid="`payroll-payment-rules-form-bracket-min-${index}`"
                    v-model="bracket.min"
                    :placeholder="t('R$ 0,00')"
                    :disabled="index > 0"
                  />
                </div>
                <div class="field-group">
                  <label>{{ t('Até') }}</label>
                  <input-currency
                    :elem-id="`payroll_bracket_max_${index}`"
                    :data-testid="`payroll-payment-rules-form-bracket-max-${index}`"
                    v-model="bracket.max"
                    :placeholder="t('R$ ∞')"
                    :disabled="index === brackets.length - 1"
                  />
                </div>
                <div class="field-group">
                  <label>{{ t('Taxa') }}</label>
                  <input-number
                    :elem-id="`payroll_bracket_rate_${index}`"
                    :data-testid="`payroll-payment-rules-form-bracket-rate-${index}`"
                    v-model="bracket.ratePercent"
                    suffix="%"
                    :min="0"
                    :max="100"
                    :step="0.25"
                  />
                </div>
                <div class="field-group-actions">
                  <Button
                    v-if="brackets.length > 1 && index < brackets.length - 1"
                    :data-testid="`payroll-payment-rules-form-bracket-remove-${index}`"
                    icon="pi pi-trash"
                    severity="danger"
                    text
                    @click="removeBracket(index)"
                    type="button"
                  />
                </div>
              </div>
            </div>
            <Button
              data-testid="payroll-payment-rules-form-bracket-add-button"
              :label="t('Adicionar Faixa')"
              icon="pi pi-plus"
              @click="addBracket"
              :disabled="brackets.length >= 10"
              type="button"
              outlined
              size="small"
            />

            <!-- Bracket Validation Errors -->
            <div v-if="bracketValidationErrors.length > 0" class="bracket-validation-errors">
              <inline-message v-for="(error, idx) in bracketValidationErrors" :key="idx" severity="error" :closable="false">
                {{ error }}
              </inline-message>
            </div>
          </div>

          <!-- Preview Calculation -->
          <div class="commission-preview">
            <h5>{{ t('Prévia de Cálculo') }}</h5>
            <div class="preview-input">
              <label>{{ t('Receita Teste') }}</label>
              <input-currency
                elem-id="payroll_form_preview_revenue"
                data-testid="payroll-payment-rules-form-preview-revenue"
                v-model="previewRevenue"
                :placeholder="t('R$ 0,00')"
              />
            </div>
            <div class="preview-result">
              <strong>{{ t('Comissão Calculada') }}:</strong>
              <span class="amount">{{ n(calculatePreviewCommission() || 0, 'currency') }}</span>
            </div>
          </div>
        </div>

        <!-- Description (optional) -->
        <div class="p-field">
          <input-rich-text
            data-testid="payroll-payment-rules-form-description-input"
            elem-id="payroll_form_description"
            v-model="formData.description"
            :label="t('Descrição')"
            :placeholder="t('Descrição ou observações opcionais')"
            :show-toolbar="true"
            min-height="150px"
          />
        </div>

        <!-- All Validation Errors -->
        <div v-if="allValidationErrors.length > 0 && !canSave" class="all-validation-errors">
          <inline-message v-for="(error, idx) in allValidationErrors" :key="idx" severity="warn" :closable="false">
            {{ error }}
          </inline-message>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConfirmAction } from '@/composables/useConfirmAction';
import { usePaymentRules } from '@/composables/usePaymentRules';
import { permissionsService } from '@/services/permissionsService';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';
import '@/contracts/Payroll_CreatePaymentRuleInput';
import type { Payroll_CreatePaymentRuleInput } from '@/contracts/Payroll_CreatePaymentRuleInput';

type Id = number | string;
type RuleType =
  | 'fixed'
  | 'commission_over_gross_revenue_executed'
  | 'commission_over_gross_revenue_sold_remotely'
  | 'commission_over_gross_revenue_sold_in_loco';
type CommissionStructureType = 'simple' | 'tiered' | 'progressive';
type PeriodType = 'weekly' | 'biweekly' | 'monthly';

interface Bracket {
  min: number;
  max: number | null;
  /** Rate stored as fraction (0..1). 5% = 0.05. PRESERVED — basis-point semantics. */
  rate: number;
  /** Rate as integer/decimal percent (0..100). 5% = 5. UI editor-only mirror of rate. */
  ratePercent: number;
}

interface PaymentRuleFormData {
  user_id: Id | null;
  franchise_id: Id | null;
  rule_type: string;
  /** Amount semantics depend on rule_type: fixed = currency value, commission = fraction (0..1). PRESERVED. */
  amount: number;
  description: string;
  meta: string | null;
}

interface PaymentRuleEntity {
  id?: Id;
  user_id?: Id;
  franchise_id?: Id;
  rule_type?: string;
  amount?: number | string;
  description?: string;
  meta?: string | null;
  active?: boolean | number | string;
}

interface SelectOption {
  ID: string;
  Name: string;
}

interface FuncionarioRecord {
  ID: Id;
  Name: string;
  [key: string]: unknown;
}

const props = defineProps<{
  rule?: PaymentRuleEntity | null;
}>();

const emit = defineEmits<{
  (e: 'saved'): void;
  (e: 'cancel'): void;
  (e: 'deleted'): void;
  (e: 'deactivated'): void;
}>();

const { t, n } = useI18n();
const { confirmAction } = useConfirmAction();
const { createRule, updateRule, deleteRule, deactivateRule, validateBrackets, isLoading } = usePaymentRules();

const isEditMode = computed<boolean>(() => !!props.rule);

// Form data
const formData = ref<PaymentRuleFormData>({
  user_id: null,
  franchise_id: null,
  rule_type: 'fixed',
  amount: 0,
  description: '',
  meta: null
});

// Period type for FIXED rules (stored in meta field)
const period_type = ref<PeriodType>('monthly');

// Commission structure state — DEFAULT brackets preserve original numeric values exactly:
// 0..29999.99 @ 3% (rate=0.03), 30000..∞ @ 5% (rate=0.05)
const commissionStructureType = ref<CommissionStructureType>('simple');
const brackets = ref<Bracket[]>([
  { min: 0, max: 29999.99, rate: 0.03, ratePercent: 3 },
  { min: 30000, max: null, rate: 0.05, ratePercent: 5 }
]);
const previewRevenue = ref<number>(45000);

// Dropdown options
const ruleTypes = ref<SelectOption[]>([
  { ID: 'fixed', Name: t('Salário fixo') },
  { ID: 'commission_over_gross_revenue_executed', Name: t('Comissão sobre receita executada') },
  { ID: 'commission_over_gross_revenue_sold_remotely', Name: t('Comissão sobre receita vendida remotamente') },
  { ID: 'commission_over_gross_revenue_sold_in_loco', Name: t('Comissão sobre receita vendida presencialmente') }
]);

const commissionStructureTypes = ref<SelectOption[]>([
  { ID: 'simple', Name: t('Simples (% único)') },
  { ID: 'tiered', Name: t('Escalonado (% muda no limite)') },
  { ID: 'progressive', Name: t('Progressivo (como IR)') }
]);

const periodTypes = ref<SelectOption[]>([
  { ID: 'weekly', Name: t('Semanal (7 dias)') },
  { ID: 'biweekly', Name: t('Quinzenal (14 dias)') },
  { ID: 'monthly', Name: t('Mensal (30 dias)') }
]);

const employees = ref<SelectOption[]>([]);

const isCommissionRule = computed<boolean>(() => {
  return !!formData.value.rule_type && formData.value.rule_type.includes('commission');
});

// Commission percentage converter (0-100 for display, 0-1 for storage)
// PRESERVED: amount in formData stays as fraction (0..1) — only display flips to percent.
const commissionPercentage = computed<number>({
  get() {
    return Math.round(formData.value.amount * 100 * 100) / 100; // Convert 0-1 to 0-100 with 2 decimals
  },
  set(value: number) {
    formData.value.amount = value / 100; // Convert 0-100 to 0-1
  }
});

// Validation computed properties
const isUserIdValid = computed<boolean>(() => {
  return formData.value.user_id !== null && formData.value.user_id !== undefined;
});

const isFranchiseIdValid = computed<boolean>(() => {
  return formData.value.franchise_id !== null && formData.value.franchise_id !== undefined;
});

const isRuleTypeValid = computed<boolean>(() => {
  return formData.value.rule_type !== null && formData.value.rule_type !== '';
});

const isAmountValid = computed<boolean>(() => {
  if (!formData.value.amount || formData.value.amount <= 0) return false;
  // For commission rules, check if amount is between 0.01 and 1.0 (100%) — PRESERVED basis-point bound
  if (isCommissionRule.value && formData.value.amount > 1.0) return false;
  return true;
});

const isPeriodTypeValid = computed<boolean>(() => {
  // Only required for FIXED rules
  if (formData.value.rule_type !== 'fixed') return true;
  return period_type.value !== null && (period_type.value as string) !== '';
});

const bracketValidationErrors = computed<string[]>(() => {
  // If simple commission or not a commission rule, no validation needed
  if (!isCommissionRule.value || commissionStructureType.value === 'simple') {
    return [];
  }

  // For tiered or progressive, validate brackets
  if (commissionStructureType.value === 'tiered' || commissionStructureType.value === 'progressive') {
    // Convert ratePercent to rate for validation — PRESERVED: pass fraction rate, not percent
    const bracketsForValidation = brackets.value.map(b => ({
      min: b.min,
      max: b.max,
      rate: b.rate
    }));

    return validateBrackets(bracketsForValidation, commissionStructureType.value) as string[];
  }

  return [];
});

const areBracketsValid = computed<boolean>(() => {
  return bracketValidationErrors.value.length === 0;
});

// All validation errors for display in UI
const allValidationErrors = computed<string[]>(() => {
  const errors: string[] = [];

  if (!isUserIdValid.value) {
    errors.push(t('Colaborador é obrigatório'));
  }
  if (!isFranchiseIdValid.value) {
    errors.push(t('Franquia é obrigatória'));
  }
  if (!isRuleTypeValid.value) {
    errors.push(t('Tipo de regra é obrigatório'));
  }
  if (!isAmountValid.value && commissionStructureType.value === 'simple') {
    errors.push(t('Valor/Taxa é obrigatório'));
  }
  if (!isPeriodTypeValid.value) {
    errors.push(t('Tipo de período é obrigatório para salário fixo'));
  }

  // Add bracket errors
  errors.push(...bracketValidationErrors.value);

  return errors;
});

const canSave = computed<boolean>(() => {
  // For structured commissions, don't require amount (brackets define it)
  const amountRequired = commissionStructureType.value === 'simple';

  return isUserIdValid.value &&
         isFranchiseIdValid.value &&
         isRuleTypeValid.value &&
         (amountRequired ? isAmountValid.value : true) &&
         isPeriodTypeValid.value &&
         areBracketsValid.value;
});

// Load employees based on selected franchise
const loadEmployees = (franchiseId: Id | null): void => {
  if (!franchiseId) {
    employees.value = [];
    log('PaymentRulesForm', 'loadEmployees', 'No franchise selected, clearing employees');
    return;
  }

  log('PaymentRulesForm', 'loadEmployees', 'Loading employees for franchise', { franchiseId });

  const funcionarios = permissionsService.getFuncionariosSelectArrayByFranquiaID(Number(franchiseId), isEditMode.value ? String(formData.value.user_id ?? '') : '') as FuncionarioRecord[];
  employees.value = funcionarios.map(func => ({
    ID: String(func.ID), // Keep as string for consistency with SelectList format
    Name: func.Name
  }));

  log('PaymentRulesForm', 'loadEmployees', 'Loaded employees', {
    count: employees.value.length,
    franchiseId
  });
};

// Watch franchise_id changes to reload employees
watch(() => formData.value.franchise_id, (newFranchiseId) => {
  loadEmployees(newFranchiseId);
  // Clear user_id when franchise changes (unless in edit mode)
  if (!isEditMode.value && formData.value.user_id) {
    formData.value.user_id = null;
  }
});

// Initialize form data on mount
onMounted(async () => {
  log('PaymentRulesForm', 'onMounted', 'Loading form data');

  // If editing, populate form
  if (props.rule) {
    formData.value = {
      ...formData.value,
      user_id: props.rule.user_id !== undefined && props.rule.user_id !== null ? String(props.rule.user_id) : null, // Keep as string for SelectList compatibility
      franchise_id: props.rule.franchise_id !== undefined && props.rule.franchise_id !== null ? parseInt(String(props.rule.franchise_id), 10) : null,
      rule_type: props.rule.rule_type ?? 'fixed',
      amount: parseFloat(String(props.rule.amount ?? 0)),
      description: props.rule.description || '',
      meta: props.rule.meta || null
    };

    // Parse meta field for commission structure or period_type
    if (props.rule.meta) {
      try {
        const meta = JSON.parse(props.rule.meta);
        // For FIXED rules, parse period_type
        if (props.rule.rule_type === 'fixed' && meta && meta.period_type) {
          period_type.value = meta.period_type as PeriodType;
        }
        // For commission rules, parse commission structure
        if (meta && meta.commission_structure) {
          commissionStructureType.value = (meta.commission_structure.type || 'simple') as CommissionStructureType;
          if (meta.commission_structure.brackets) {
            // PRESERVED: rate kept as fraction; ratePercent derived for the editor (rate * 100)
            brackets.value = (meta.commission_structure.brackets as Array<{ min: number; max: number | null; rate: number }>).map(b => ({
              min: b.min,
              max: b.max,
              rate: b.rate,
              ratePercent: Math.round(b.rate * 100 * 100) / 100 // Convert to percentage
            }));
          }
        }
      } catch (e) {
        log('PaymentRulesForm', 'onMounted', 'Error parsing meta field', e);
      }
    }

    // Load employees for the rule's franchise
    loadEmployees(formData.value.franchise_id);
  }
});

// Watch rule type to reset amount when switching between fixed/commission
// Only reset in create mode, not when editing existing rules
watch(() => formData.value.rule_type, (newType, oldType) => {
  if (!isEditMode.value && oldType && newType !== oldType) {
    formData.value.amount = 0;
    // Reset commission structure to simple
    commissionStructureType.value = 'simple';
  }
});

// Watch brackets and commission structure type to update ratePercent and meta field
// Also watch period_type for FIXED rules
watch([commissionStructureType, brackets, period_type, () => formData.value.rule_type], () => {
  // Update rate from ratePercent for all brackets — PRESERVED basis-point conversion
  brackets.value.forEach(bracket => {
    bracket.rate = bracket.ratePercent / 100;
  });

  // Serialize to meta field
  if (formData.value.rule_type === 'fixed') {
    // For FIXED rules, store period_type in meta
    formData.value.meta = JSON.stringify({
      period_type: period_type.value
    });
  } else if (commissionStructureType.value === 'simple' || !isCommissionRule.value) {
    formData.value.meta = null;
  } else {
    formData.value.meta = JSON.stringify({
      commission_structure: {
        type: commissionStructureType.value,
        brackets: brackets.value.map(b => ({
          min: b.min,
          max: b.max,
          rate: b.rate
        }))
      }
    });
  }
}, { deep: true });

// Helper functions for commission structure
const getCommissionStructureHelp = (): string => {
  if (commissionStructureType.value === 'simple') {
    return t('Aplica uma única taxa de comissão sobre toda a receita');
  } else if (commissionStructureType.value === 'tiered') {
    return t('Quando a receita atinge um limite, muda a taxa aplicada sobre TODA a receita');
  } else if (commissionStructureType.value === 'progressive') {
    return t('Como o IR: diferentes taxas para diferentes faixas de receita');
  }
  return '';
};

const addBracket = (): void => {
  const lastBracket = brackets.value[brackets.value.length - 1];
  if (lastBracket.max === null) {
    // Set a max for the current last bracket
    lastBracket.max = (lastBracket.min || 0) + 10000;
  }

  brackets.value.push({
    min: lastBracket.max + 0.01,
    max: null,
    rate: 0.05,
    ratePercent: 5
  });
};

const removeBracket = (index: number): void => {
  brackets.value.splice(index, 1);
  // Ensure last bracket has max = null
  if (brackets.value.length > 0) {
    brackets.value[brackets.value.length - 1].max = null;
  }
};

const calculatePreviewCommission = (): number => {
  if (!previewRevenue.value || previewRevenue.value <= 0) {
    return 0;
  }

  const revenue = Number(previewRevenue.value);

  // Simple / non-commission: amount * revenue (amount is fraction 0..1 for commission)
  if (commissionStructureType.value === 'simple' || !isCommissionRule.value) {
    return revenue * (formData.value.amount || 0);
  }

  if (commissionStructureType.value === 'tiered') {
    // Find applicable bracket — entire revenue gets that bracket's rate
    for (const bracket of brackets.value) {
      if (revenue >= bracket.min && (bracket.max === null || revenue <= bracket.max)) {
        return revenue * bracket.rate;
      }
    }
    return 0;
  }

  if (commissionStructureType.value === 'progressive') {
    // Progressive: each bracket's portion taxed at its rate (IR-style)
    let total = 0;
    for (const bracket of brackets.value) {
      const min = bracket.min;
      const max = bracket.max ?? revenue;
      const amount = Math.min(revenue, max) - min;
      if (amount > 0) {
        total += amount * bracket.rate;
      }
      if (max >= revenue) break;
    }
    return total;
  }

  return 0;
};


const handleSubmit = async (): Promise<void> => {
  log('PaymentRulesForm', 'handleSubmit', 'Submitting form', { formData: formData.value });

  try {
    const submitData: PaymentRuleFormData = {
      ...formData.value
    };

    // For structured commissions (tiered/progressive), set amount to first bracket's rate
    // The backend requires amount > 0, but actual calculation uses brackets from meta
    // PRESERVED: amount kept as fraction (rate is fraction)
    if (commissionStructureType.value !== 'simple' && isCommissionRule.value) {
      submitData.amount = brackets.value[0]?.rate || 0.01;
    }

    if (isEditMode.value && props.rule) {
      // Only send editable fields (amount, description, meta)
      await updateRule(Number(props.rule.id), {
        amount: submitData.amount,
        description: submitData.description,
        meta: submitData.meta ?? undefined
      });
      eventBus.emit('payment-rule.updated', props.rule.id);
    } else {
      const createPayload: Payroll_CreatePaymentRuleInput = {
        user_id: String(submitData.user_id ?? ''),
        franchise_id: Number(submitData.franchise_id),
        rule_type: submitData.rule_type,
        amount: submitData.amount,
        description: submitData.description || undefined,
        meta: submitData.meta ?? undefined
      };
      const newRule = await createRule(createPayload) as unknown as { id: Id } | null;
      if (newRule) {
        eventBus.emit('payment-rule.created', newRule.id);
      }
    }

    emit('saved');
  } catch (error) {
    const err = error as Error;
    log('PaymentRulesForm', 'handleSubmit', 'Error saving payment rule', err);
  }
};

const handleCancel = (): void => {
  emit('cancel');
};

const handleDelete = (): void => {
  if (!props.rule) return;
  const ruleId = props.rule.id;
  if (ruleId === undefined || ruleId === null) return;

  confirmAction({
    message: t('Tem certeza que deseja excluir esta regra de pagamento permanentemente?'),
    header: t('Excluir regra de pagamento'),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('Sim, excluir'),
    rejectLabel: t('Cancelar'),
    onAccept: async () => {
      try {
        await deleteRule(Number(ruleId));
        eventBus.emit('payment-rule.deleted', ruleId);
        eventBus.emit('globalMessage.success', {
          message: t('Regra de pagamento excluída com sucesso'),
          duration: 3000
        });
        emit('deleted');
      } catch (error) {
        const err = error as Error;
        log('PaymentRulesForm', 'handleDelete', 'Error deleting payment rule', err);
        eventBus.emit('globalMessage.error', {
          message: err.message || t('Falha ao excluir regra de pagamento'),
          duration: 5000
        });
      }
    }
  });
};

const handleDeactivate = async (): Promise<void> => {
  if (!props.rule) return;
  try {
    await deactivateRule(Number(props.rule.id));
    eventBus.emit('payment-rule.deactivated', props.rule.id);
    eventBus.emit('globalMessage.success', {
      message: t('Regra de pagamento desativada com sucesso'),
      duration: 3000
    });
    emit('deactivated');
  } catch (error) {
    const err = error as Error;
    log('PaymentRulesForm', 'handleDeactivate', 'Error deactivating payment rule', err);
    eventBus.emit('globalMessage.error', {
      message: err.message || t('Falha ao desativar regra de pagamento'),
      duration: 5000
    });
  }
};

// Expose methods and computed for parent component (Modal)
defineExpose({
  canSave,
  handleSubmit
});
</script>

<style scoped>
.payment-rules-form {
  padding: 1rem;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--surface-border);
}

.p-field {
  margin-bottom: 1.5rem;
}

.p-help {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.commission-slider-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.commission-value-display {
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--primary-color);
  padding: 0.5rem;
  background: var(--surface-50);
  border-radius: 0.375rem;
  border: 2px solid var(--primary-color);
}

.slider-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: var(--text-color-secondary);
  margin-top: -0.5rem;
}

/* Bracket Editor Styles */
.bracket-editor {
  padding: 1rem;
  background: var(--surface-50);
  border-radius: 0.375rem;
  border: 1px solid var(--surface-border);
}

.bracket-row {
  margin-bottom: 1rem;
  padding: 1rem;
  background: var(--surface-0);
  border-radius: 0.375rem;
  border: 1px solid var(--surface-border);
}

.bracket-fields {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 1rem;
  align-items: end;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.field-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-color-secondary);
}

.field-group-actions {
  display: flex;
  align-items: center;
  padding-bottom: 0.25rem;
}

.commission-preview {
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: var(--surface-0);
  border-radius: 0.375rem;
  border: 2px solid var(--primary-color);
}

.commission-preview h5 {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--primary-color);
}

.preview-input {
  margin-bottom: 1rem;
}

.preview-input label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.preview-result {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: var(--surface-50);
  border-radius: 0.375rem;
  font-size: 1.125rem;
}

.preview-result .amount {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
}

.bracket-validation-errors,
.all-validation-errors {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.bracket-validation-errors :deep(.p-message),
.all-validation-errors :deep(.p-message) {
  margin: 0;
}

@media (max-width: 768px) {
  .bracket-fields {
    grid-template-columns: 1fr;
  }

  .field-group-actions {
    justify-content: flex-end;
  }
}
</style>
